import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CreditCard, Plus, Loader2, Lock, ShieldAlert, Upload, Gift, Repeat, Landmark,
  AlertTriangle, FileBarChart, Pencil, Trash2, Search, X,
} from "lucide-react";
import { Link } from "react-router-dom";
import CardSkin from "@/components/finance/CardSkin";
import CardArtworkStudio from "@/components/finance/CardArtworkStudio";
import PaymentCentre, { RecordPaymentDrawer } from "@/components/finance/PaymentCentre";
import StatementIntake from "@/components/finance/StatementIntake";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const NET_LABEL = { VISA: "VISA", MASTERCARD: "Mastercard", AMEX: "AMEX", RUPAY: "RuPay", DINERS: "Diners", OTHER: "" };

// Small non-intrusive status chips (never cover artwork with big warnings).
function cardBadges(c) {
  const out = [];
  const util = c.utilisation_pct || 0;
  const st = c.status;
  if (st === "CLOSED" || st === "REPLACED" || st === "EXPIRED" || st === "LOST_STOLEN")
    out.push({ t: st.replace(/_/g, " "), c: "bg-slate-200/90 text-slate-700" });
  else if (st === "TEMPORARILY_BLOCKED") out.push({ t: "Blocked", c: "bg-amber-200/90 text-amber-900" });
  if (util >= 80) out.push({ t: "High use", c: "bg-rose-500/90 text-white" });
  if (c.expiry_year && c.expiry_month) {
    const exp = new Date(c.expiry_year, c.expiry_month, 0);
    const soon = (exp - new Date()) / 86400000;
    if (soon > 0 && soon < 90) out.push({ t: "Expiring", c: "bg-amber-400/90 text-amber-950" });
  }
  return out;
}

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().slice(0, 10);

const NETWORKS = ["VISA", "MASTERCARD", "AMEX", "RUPAY", "DINERS", "OTHER"];
const CARD_TYPES = ["CREDIT", "DEBIT"];
const CARD_STATUSES = ["ACTIVE", "TEMPORARILY_BLOCKED", "REPLACEMENT_PENDING", "REPLACED",
  "CLOSED", "EXPIRED", "LOST_STOLEN"];
const STATUS_TONE = {
  DUE: "bg-amber-50 text-amber-700 border-amber-200",
  DUE_SOON: "bg-amber-50 text-amber-700 border-amber-200",
  OVERDUE: "bg-rose-50 text-rose-700 border-rose-200",
  PAID: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PARTIALLY_PAID: "bg-indigo-50 text-indigo-700 border-indigo-200",
  UNPAID: "bg-slate-50 text-slate-600 border-slate-200",
  OVERPAID: "bg-violet-50 text-violet-700 border-violet-200",
  WAIVED: "bg-teal-50 text-teal-700 border-teal-200",
  CLOSED: "bg-slate-100 text-slate-600 border-slate-200",
  NOT_DUE: "bg-slate-50 text-slate-600 border-slate-200",
  REVIEW_REQUIRED: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function CardControl() {
  const [access, setAccess] = useState(null);
  const [overview, setOverview] = useState(null);
  const [cards, setCards] = useState([]);
  const [sel, setSel] = useState(null);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [walletFilter, setWalletFilter] = useState("ALL");
  const [walletQ, setWalletQ] = useState("");

  const loadCards = useCallback(async () => {
    try {
      const { data } = await api.get("/card-control/cards");
      setCards(data.items || []);
      const ov = await api.get("/card-control/overview"); setOverview(ov.data);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/card-control/access");
        setAccess(data);
        if (data.can_access) loadCards();
      } catch { setAccess({ can_access: false }); }
    })();
  }, [loadCards]);

  if (access === null) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!access.can_access) return (
    <div className="p-10 text-center" data-testid="card-control-denied">
      <ShieldAlert className="h-10 w-10 text-rose-500 mx-auto mb-2" />
      <div className="font-semibold">Card Control is restricted</div>
      <p className="text-sm text-slate-500">Only Finance administrators can access this workspace.</p>
    </div>
  );

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="card-control-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-indigo-700" /> Credit Card Control
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 flex items-center gap-1"><Lock className="h-3 w-3" /> Restricted</span>
          </h1>
          <p className="text-sm text-slate-500">ONE Finance Vault · statements, spends, payments, rewards & EMI. Card numbers are always masked.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative" data-testid="card-number-search-box">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={walletQ}
              onChange={(e) => setWalletQ(e.target.value)}
              placeholder="Search card number…"
              data-testid="card-number-search"
              inputMode="numeric"
              className="h-9 text-[13px] border border-slate-200 rounded-md pl-8 pr-7 w-56 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
            />
            {walletQ && (
              <button
                type="button"
                onClick={() => setWalletQ("")}
                data-testid="card-number-search-clear"
                aria-label="Clear card number search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Link to="/finance/card-reports"><Button variant="outline" data-testid="card-reports-link"><FileBarChart className="h-4 w-4 mr-1" /> Reports</Button></Link>
          <Button className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={() => setAddCardOpen(true)} data-testid="card-add-btn"><Plus className="h-4 w-4 mr-1" /> Add card</Button>
        </div>
      </div>

      {overview && <OverviewTiles ov={overview} />}

      <div className="mt-5">
        <PaymentCentre overview={overview} onChanged={loadCards} onOpenCard={(id) => setSel(id)} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6 mb-3">
        <h2 className="text-lg font-heading font-semibold text-slate-900 mr-2" data-testid="card-wallet-title">Card Wallet</h2>
        {["ALL", "PERSONAL", "BUSINESS", "CORPORATE"].map((f) => (
          <button key={f} onClick={() => setWalletFilter(f)} data-testid={`wallet-filter-${f.toLowerCase()}`}
            className={"text-[11px] px-2.5 py-1 rounded-full border transition-colors " +
              (walletFilter === f ? "bg-indigo-600 text-white border-indigo-600"
                                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")}>
            {f === "ALL" ? "All" : f[0] + f.slice(1).toLowerCase()}
          </button>
        ))}
        <input value={walletQ} onChange={(e) => setWalletQ(e.target.value)}
          placeholder="Search card, bank, last 4, network…" data-testid="wallet-search"
          className="ml-auto h-8 text-[12px] border border-slate-200 rounded-md px-3 w-64" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="card-tiles">
        {(() => {
          const q = walletQ.trim().toLowerCase();
          const list = cards.filter((c) => {
            if (walletFilter !== "ALL" && (c.scope || "BUSINESS") !== walletFilter) return false;
            if (!q) return true;
            return [c.card_name, c.issuer_bank, c.network, c.holder_name, c.last4, c.masked_number]
              .some((v) => (v || "").toString().toLowerCase().includes(q));
          });
          if (!list.length) return <div className="text-sm text-slate-400 col-span-4 py-6">No cards match.</div>;
          return list.map((c) => (
            <button key={c.id} onClick={() => setSel(c.id)} data-testid={`card-tile-${c.id}`}
              className={`group relative text-left rounded-2xl overflow-hidden aspect-[1.6/1] transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5 ${sel === c.id ? "ring-2 ring-indigo-400" : ""}`}>
              {c.artwork_url ? (
                <img src={`${BACKEND}${c.artwork_url}`} alt={c.card_name}
                  className="absolute inset-0 w-full h-full object-cover" data-testid={`card-art-${c.id}`} />
              ) : (
                <div data-testid={`card-fallback-${c.id}`}><CardSkin card={c} variant="tile" /></div>
              )}
              {/* legibility scrim only across the bottom third (keeps artwork clean) */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
              <div className="relative h-full p-3.5 flex flex-col justify-end text-white">
                <div className="font-mono text-[15px] tracking-[0.15em] drop-shadow mb-1.5">{c.masked_number}</div>
                <div className="flex justify-between items-end gap-2">
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold truncate drop-shadow">{c.card_name}</div>
                    <div className="text-[9px] text-white/80 truncate">{(c.scope || "BUSINESS")[0] + (c.scope || "BUSINESS").slice(1).toLowerCase()} · {c.card_type || "CREDIT"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[8px] uppercase text-white/70 leading-none">Available</div>
                    <div className="text-[12px] font-semibold leading-tight">{money(c.available_limit)}</div>
                    <div className="text-[8px] text-white/70 leading-none mt-0.5">Outstanding {money(c.outstanding)}</div>
                  </div>
                </div>
                {cardBadges(c).length > 0 && (
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    {cardBadges(c).map((b, i) => (
                      <span key={i} className={`text-[8px] px-1.5 py-0.5 rounded-full font-medium ${b.c}`}>{b.t}</span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ));
        })()}
        {!cards.length && <div className="text-sm text-slate-400 col-span-4 py-6">No cards yet — add one to begin.</div>}
      </div>

      {sel && <Card360 cardId={sel} canPersonal={access.can_personal} onChange={loadCards} onClose={() => setSel(null)} />}

      {addCardOpen && <AddCard canPersonal={access.can_personal} onClose={() => setAddCardOpen(false)}
        onSaved={() => { setAddCardOpen(false); loadCards(); }} />}
    </div>
  );
}

function OverviewTiles({ ov }) {
  const tiles = [
    { l: "Active cards", v: ov.active_cards, plain: true },
    { l: "Total limit", v: money(ov.total_credit_limit) },
    { l: "Outstanding", v: money(ov.current_outstanding), tone: "text-rose-700" },
    { l: "Available", v: money(ov.available_limit), tone: "text-emerald-700" },
    { l: "Utilisation", v: `${ov.utilisation_pct}%` , plain: true },
    { l: "Payments due", v: ov.statements_due, plain: true, tone: ov.statements_due ? "text-amber-700" : "" },
    { l: "Overdue", v: ov.statements_overdue, plain: true, tone: ov.statements_overdue ? "text-rose-700" : "" },
    { l: "Due amount", v: money(ov.due_total), tone: ov.due_total ? "text-amber-700" : "" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="card-overview">
      {tiles.map((t) => (
        <div key={t.l} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-slate-400">{t.l}</div>
          <div className={`text-base font-semibold ${t.tone || "text-slate-800"}`}>{t.v}</div>
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "transactions", label: "Transactions" },
  { key: "statements", label: "Statements" },
  { key: "payments", label: "Payments" },
  { key: "rewards", label: "Rewards" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "emis", label: "EMI" },
];

function Card360({ cardId, canPersonal, onChange, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dlg, setDlg] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/card-control/cards/${cardId}/360`); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, [cardId]);
  useEffect(() => { load(); }, [load]);
  const artInput = useRef(null);
  const [artBusy, setArtBusy] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  if (!data) return <div className="mt-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const c = data.card;
  const refresh = () => { load(); onChange?.(); };
  const uploadArt = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) {
      toast.error("Artwork must be PNG, JPG or WebP"); return;
    }
    setArtBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/card-control/cards/${cardId}/artwork`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Card artwork updated"); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setArtBusy(false); if (artInput.current) artInput.current.value = "";
  };
  const removeArt = async () => {
    setArtBusy(true);
    try { await api.delete(`/card-control/cards/${cardId}/artwork`); toast.success("Artwork removed — using fallback"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Remove failed"); }
    setArtBusy(false);
  };
  const doClose = () => {
    const reason = window.prompt(`Close card ${c.card_name}? It keeps history but stops new use. Reason:`);
    if (!reason) return;
    api.post(`/card-control/cards/${cardId}/close`, { reason })
      .then(() => { toast.success("Card closed"); refresh(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Close failed"));
  };
  const doDelete = () => {
    const reason = window.prompt(`Delete card ${c.card_name}? Only unused cards can be deleted. Reason:`);
    if (!reason) return;
    api.delete(`/card-control/cards/${cardId}?reason=${encodeURIComponent(reason)}`)
      .then(() => { toast.success("Card deleted"); onChange?.(); onClose(); })
      .catch((e) => {
        const d = e.response?.data?.detail;
        toast.error((typeof d === "object" ? d.message : d) || "Delete failed");
      });
  };

  return (
    <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden" data-testid="card-360">
      <div className="bg-slate-50 px-4 py-3 flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative w-32 aspect-[1.6/1] rounded-lg overflow-hidden shrink-0 shadow-sm" data-testid="card-360-art">
            {c.artwork_url
              ? <img src={`${BACKEND}${c.artwork_url}`} alt={c.card_name} className="absolute inset-0 w-full h-full object-cover" />
              : <CardSkin card={c} variant="detail" />}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-1 left-1.5 right-1.5 text-white">
              <div className="font-mono text-[9px] tracking-wider">{c.masked_number}</div>
              <div className="text-[8px] text-white/80 truncate">{NET_LABEL[c.network] ?? c.network}</div>
            </div>
          </div>
          <div className="min-w-0">
            <div className="font-semibold flex items-center gap-2 flex-wrap">{c.card_name}
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200">{c.status}</span>
            </div>
            <div className="text-[11px] text-slate-500">{c.issuer_bank} · {c.network} · {c.scope} · limit {money(c.credit_limit)} · outstanding {money(c.outstanding)}</div>
            <div className="flex items-center gap-1.5 mt-1.5">
              <input ref={artInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={uploadArt} data-testid="card-artwork-input" />
              <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={artBusy}
                onClick={() => setStudioOpen(true)} data-testid="card-artwork-upload">
                {artBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                {c.artwork_url ? "Replace with photo" : "Upload card photo"}
              </Button>
              <button className="text-[10px] text-slate-400 underline decoration-dotted" disabled={artBusy}
                onClick={() => artInput.current?.click()} data-testid="card-artwork-quick">quick upload</button>
              {c.artwork_url && (
                <Button variant="ghost" size="sm" className="h-7 text-[11px] text-slate-500" disabled={artBusy}
                  onClick={removeArt} data-testid="card-artwork-remove">Remove</Button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={() => setDlg("editcard")} data-testid="card-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
          {c.status === "ACTIVE" && <Button variant="outline" size="sm" onClick={doClose} data-testid="card-close-btn">Close</Button>}
          <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={doDelete} data-testid="card-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="card-360-close">Close panel</Button>
        </div>
      </div>
      <div className="flex gap-1 border-b border-slate-200 px-3 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`card-tab-${t.key}`}
            className={`px-3 py-2 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px ${tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="p-4">
        {tab === "overview" && <OverviewTab c={c} data={data} />}
        {tab === "transactions" && <TxnTab cardId={cardId} rows={data.transactions} onAdd={() => setDlg("txn")} onImport={() => setDlg("import")} onChange={refresh} />}
        {tab === "statements" && <StatementTab cardId={cardId} rows={data.statements} onAdd={() => setDlg("stmt")} onChange={refresh} />}
        {tab === "payments" && <PaymentTab rows={data.payments} onAdd={() => setDlg("pay")} onChange={refresh} />}
        {tab === "rewards" && <RewardTab cardId={cardId} onAdd={() => setDlg("reward")} />}
        {tab === "subscriptions" && <SubTab rows={data.subscriptions} onAdd={() => setDlg("sub")} />}
        {tab === "emis" && <EmiTab rows={data.emis} onAdd={() => setDlg("emi")} />}
      </div>

      {dlg === "txn" && <AddTxn cardId={cardId} categories={data.categories} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {dlg === "stmt" && <StatementIntake cardId={cardId} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {dlg === "pay" && <AddPayment cardId={cardId} statements={data.statements} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {dlg === "reward" && <AddReward cardId={cardId} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {dlg === "sub" && <AddSubscription cardId={cardId} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {dlg === "emi" && <AddEmi cardId={cardId} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {dlg === "import" && <ImportTxns cardId={cardId} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {dlg === "editcard" && <AddCard canPersonal={canPersonal} editing={c} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {studioOpen && <CardArtworkStudio cardId={cardId} onClose={() => setStudioOpen(false)} onSaved={() => { setStudioOpen(false); refresh(); }} />}
    </div>
  );
}

function OverviewTab({ c }) {
  const rows = [
    ["Status", c.status || "ACTIVE"],
    ["Card type", c.card_type || "CREDIT"], ["Name on card", c.name_on_card || "—"],
    ["Credit limit", money(c.credit_limit)], ["Cash limit", c.cash_limit != null ? money(c.cash_limit) : "—"],
    ["Outstanding", money(c.outstanding)], ["Available", money(c.available_limit)],
    ["Utilisation", `${c.utilisation_pct}%`], ["Statement date", c.statement_date || "—"],
    ["Due rule", c.due_date_rule || "—"], ["Autopay", c.autopay ? "On" : "Off"],
    ["Annual fee", c.annual_fee != null ? money(c.annual_fee) : "—"], ["Fee month", c.annual_fee_month || "—"],
    ["Expiry", c.expiry_month ? `${String(c.expiry_month).padStart(2, "0")}/${c.expiry_year || "--"}` : "—"],
    ["Holder", c.holder_name || "—"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="card-overview-tab">
      {rows.map(([l, v]) => (
        <div key={l}><div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div><div className="text-[14px] font-medium">{v}</div></div>
      ))}
    </div>
  );
}

function TableShell({ headers, children, addLabel, onAdd, extra, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex justify-end gap-2 mb-2">
        {extra}
        {onAdd && <Button size="sm" className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={onAdd} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5 mr-1" />{addLabel}</Button>}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
            <tr>{headers.map((h) => <th key={h} className="px-2 py-2 text-left whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">{children}</tbody>
        </table>
      </div>
    </div>
  );
}

function TxnTab({ rows, onAdd, onImport, onChange }) {
  const del = async (id) => {
    const reason = window.prompt("Reason to delete this transaction?"); if (!reason) return;
    try { await api.delete(`/card-control/txns/${id}?reason=${encodeURIComponent(reason)}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed"); }
  };
  return (
    <TableShell testid="card-txns" addLabel="Add spend" onAdd={onAdd}
      extra={<Button size="sm" variant="outline" onClick={onImport} data-testid="card-txns-import"><Upload className="h-3.5 w-3.5 mr-1" />Import</Button>}
      headers={["Date", "Merchant", "Category", "Use", "Amount", "Kind", "Recon", ""]}>
      {rows.map((t, i) => (
        <tr key={t.id} data-testid={`card-txn-row-${i}`} className="hover:bg-slate-50">
          <td className="px-2 py-1.5 whitespace-nowrap">{t.txn_date}</td>
          <td className="px-2 py-1.5 max-w-[220px] truncate">{t.merchant}</td>
          <td className="px-2 py-1.5">{t.category || "—"}</td>
          <td className="px-2 py-1.5 text-[10px]">{t.use}</td>
          <td className={`px-2 py-1.5 text-right font-medium ${t.kind === "REFUND" || t.kind === "CASHBACK" ? "text-emerald-700" : ""}`}>{money(t.amount)}</td>
          <td className="px-2 py-1.5 text-[10px]">{t.kind}</td>
          <td className="px-2 py-1.5 text-[10px]">{t.recon_status}</td>
          <td className="px-2 py-1.5"><button className="text-rose-500 text-[10px]" onClick={() => del(t.id)} data-testid={`card-txn-del-${i}`}>Delete</button></td>
        </tr>
      ))}
      {!rows.length && <tr><td colSpan={8} className="px-2 py-8 text-center text-slate-400">No transactions.</td></tr>}
    </TableShell>
  );
}

function StatementTab({ rows, onAdd, onChange }) {
  const [recon, setRecon] = useState(null);
  const [pay, setPay] = useState(null);
  const [edit, setEdit] = useState(null);
  const [settle, setSettle] = useState(null);
  const [del, setDel] = useState(null);
  return (
    <>
    <TableShell testid="card-statements" addLabel="Add statement" onAdd={onAdd}
      headers={["Statement", "Period", "Stmt date", "Due date", "Target", "Total due", "Paid", "Outstanding", "Status", "Actions"]}>
      {rows.map((s, i) => (
        <tr key={s.id} data-testid={`card-stmt-row-${i}`} className="hover:bg-slate-50">
          <td className="px-2 py-1.5 font-mono text-[10px]">{s.code}</td>
          <td className="px-2 py-1.5">{s.period_label || "—"}</td>
          <td className="px-2 py-1.5">{s.statement_date}</td>
          <td className="px-2 py-1.5">{s.due_date}</td>
          <td className="px-2 py-1.5 text-[10px] text-slate-500">{s.target_pay_by || "—"}</td>
          <td className="px-2 py-1.5 text-right">{money(s.total_due)}</td>
          <td className="px-2 py-1.5 text-right text-emerald-700">{money(s.paid_amount)}</td>
          <td className="px-2 py-1.5 text-right font-medium">{money(s.outstanding)}</td>
          <td className="px-2 py-1.5"><span className={`text-[9px] px-1.5 py-0.5 rounded border ${STATUS_TONE[s.status] || "bg-slate-100"}`}>{s.status}</span>{s.reconciled && <span className="text-[8px] ml-1 text-emerald-600">✓ recon</span>}</td>
          <td className="px-2 py-1.5 whitespace-nowrap">
            <div className="flex items-center gap-1.5 text-[10px] font-medium">
              {!["PAID", "WAIVED", "CLOSED"].includes(s.status) && (
                <button className="text-indigo-600 hover:underline" onClick={() => setPay(s)} data-testid={`card-stmt-pay-${i}`}>Pay</button>)}
              <button className="text-slate-500 hover:underline" onClick={() => setEdit(s)} data-testid={`card-stmt-edit-${i}`}>Edit</button>
              <button className="text-indigo-600 hover:underline" onClick={() => setRecon(s)} data-testid={`card-stmt-recon-${i}`}>Reconcile</button>
              {!["WAIVED", "CLOSED"].includes(s.status) && (
                <button className="text-amber-600 hover:underline" onClick={() => setSettle(s)} data-testid={`card-stmt-settle-${i}`}>Settle</button>)}
              <button className="text-rose-600 hover:underline" onClick={() => setDel(s)} data-testid={`card-stmt-delete-${i}`}>Delete</button>
            </div>
          </td>
        </tr>
      ))}
      {!rows.length && <tr><td colSpan={10} className="px-2 py-8 text-center text-slate-400">No statements.</td></tr>}
    </TableShell>
    {recon && <ReconcileDialog statement={recon} onClose={() => setRecon(null)} onChange={() => { setRecon(null); onChange(); }} />}
    {pay && <RecordPaymentDrawer row={{ statement_id: pay.id, card_id: pay.card_id, card_name: pay.code, last4: "", period_label: pay.period_label || pay.statement_date, outstanding: pay.outstanding, total_due: pay.total_due, target_pay_by: pay.target_pay_by }} onClose={() => setPay(null)} onSaved={() => { setPay(null); onChange(); }} />}
    {edit && <EditStatementDialog statement={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChange(); }} />}
    {settle && <SettleDialog statement={settle} onClose={() => setSettle(null)} onSaved={() => { setSettle(null); onChange(); }} />}
    {del && <DeleteStatementDialog statement={del} onClose={() => setDel(null)} onDeleted={() => { setDel(null); onChange(); }} />}
    </>
  );
}

function EditStatementDialog({ statement, onClose, onSaved }) {
  const [f, setF] = useState({
    target_pay_by: statement.target_pay_by || "",
    carry_forward_status: statement.carry_forward_status || "UNKNOWN",
    reconcile_status: statement.reconcile_status || "UNMATCHED",
    notes: statement.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/card-control/statements/${statement.id}`, f); toast.success("Statement updated"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stmt-edit-dialog">
        <DialogHeader><DialogTitle className="text-[15px]">Edit statement · {statement.code}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[12px]">Target Pay By</Label>
            <Input type="date" value={f.target_pay_by} onChange={(e) => setF({ ...f, target_pay_by: e.target.value })} data-testid="stmt-edit-target" className="mt-1" /></div>
          <div><Label className="text-[12px]">Carry-forward status</Label>
            <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white mt-1" value={f.carry_forward_status} onChange={(e) => setF({ ...f, carry_forward_status: e.target.value })} data-testid="stmt-edit-carry">
              <option value="UNKNOWN">Unknown / Review</option>
              <option value="INCLUDED">Appears included in current</option>
              <option value="NOT_INCLUDED">Not included in current</option>
            </select></div>
          <div><Label className="text-[12px]">Reconciliation</Label>
            <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white mt-1" value={f.reconcile_status} onChange={(e) => setF({ ...f, reconcile_status: e.target.value })} data-testid="stmt-edit-recon">
              {["UNMATCHED", "PARTIALLY_MATCHED", "MATCHED", "REVIEW_REQUIRED"].map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
            </select></div>
          <div className="col-span-2"><Label className="text-[12px]">Notes</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={save} disabled={busy} data-testid="stmt-edit-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettleDialog({ statement, onClose, onSaved }) {
  const [f, setF] = useState({ status: "WAIVED", reason: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try { await api.post(`/card-control/statements/${statement.id}/settle`, f); toast.success("Statement settled — reminders stopped"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stmt-settle-dialog">
        <DialogHeader><DialogTitle className="text-[15px]">Mark statement settled · {statement.code}</DialogTitle></DialogHeader>
        <div className="text-[11px] text-slate-500 -mt-2">Stops reminders and removes it from Payment Priority. Use for bank adjustments, credit notes, waivers, disputes.</div>
        <div className="space-y-3">
          <div><Label className="text-[12px]">Settlement type</Label>
            <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white mt-1" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="stmt-settle-type">
              <option value="WAIVED">Waived / Adjusted</option>
              <option value="CLOSED">Closed</option>
            </select></div>
          <div><Label className="text-[12px]">Reason *</Label>
            <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Bank adjustment / credit note / waiver / dispute" data-testid="stmt-settle-reason" className="mt-1" /></div>
          <div><Label className="text-[12px]">Notes</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={save} disabled={busy} data-testid="stmt-settle-save">{busy ? "Saving…" : "Mark settled"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteStatementDialog({ statement, onClose, onDeleted }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try { await api.delete(`/card-control/statements/${statement.id}?reason=${encodeURIComponent(reason)}`); toast.success("Statement deleted"); onDeleted(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="stmt-delete-dialog">
        <DialogHeader><DialogTitle className="text-[15px]">Delete statement · {statement.code}</DialogTitle></DialogHeader>
        <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          This permanently deletes the statement and its {money(statement.paid_amount)} of recorded payments, reverting the card outstanding. This cannot be undone.
        </div>
        <div><Label className="text-[12px]">Reason *</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being deleted?" data-testid="stmt-delete-reason" className="mt-1" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={go} disabled={busy} data-testid="stmt-delete-confirm">{busy ? "Deleting…" : "Delete statement"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReconcileDialog({ statement, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/card-control/statements/${statement.id}/reconcile`); setData(data); setSel({}); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, [statement.id]);
  useEffect(() => { load(); }, [load]);
  const auto = async () => { try { const { data } = await api.post(`/card-control/statements/${statement.id}/reconcile/auto`); toast.success(`Tied ${data.tied} spends`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const tie = async () => { const ids = Object.keys(sel).filter((k) => sel[k]); if (!ids.length) return; try { await api.post(`/card-control/statements/${statement.id}/tie`, { txn_ids: ids }); toast.success("Tied"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const untie = async (id) => { try { await api.post(`/card-control/statements/${statement.id}/untie`, { txn_ids: [id] }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const [override, setOverride] = useState(null); // {message} when out of balance
  const [overrideReason, setOverrideReason] = useState("");
  const confirm = async () => {
    try { await api.post(`/card-control/statements/${statement.id}/reconcile/confirm`, {}); toast.success("Statement reconciled"); onChange(); }
    catch (e) {
      // NOTE: the axios interceptor in lib/api.js flattens object `detail`
      // into a string and keeps the original on `_rawDetail`.
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (d?.variance !== undefined) { setOverride({ message: d.message }); }
      else { toast.error(typeof d === "string" ? d : "Failed"); }
    }
  };
  const confirmOverride = async () => {
    if (!overrideReason.trim()) { toast.error("An override reason is required"); return; }
    try { await api.post(`/card-control/statements/${statement.id}/reconcile/confirm`, { override_reason: overrideReason }); toast.success("Reconciled with override"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="card-reconcile-dialog">
        <DialogHeader><DialogTitle>Reconcile {statement.code} · due {statement.due_date}</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["Statement total", money(data.expected)], ["Tied total", money(data.tied_total)],
                ["Variance", money(data.variance)], ["Duplicates", data.duplicate_count]].map(([l, v], i) => (
                <div key={l} className={`rounded-lg border px-2 py-1.5 ${i === 2 && !data.in_balance ? "border-rose-200 bg-rose-50" : "border-slate-200"}`}>
                  <div className="text-[9px] uppercase text-slate-400">{l}</div><div className="text-[13px] font-semibold">{v}</div>
                </div>
              ))}
            </div>
            {data.missing_amount > 0 && <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Missing {money(data.missing_amount)} — on the statement but not yet recorded as spends.</div>}
            {data.extra_amount > 0 && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Extra {money(data.extra_amount)} — tied spends exceed the statement (possible duplicate).</div>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1"><span className="text-[12px] font-semibold">Suggested ({data.unmatched_count})</span>
                  <div className="flex gap-1"><Button size="sm" variant="outline" onClick={tie} data-testid="card-recon-tie">Tie selected</Button><Button size="sm" onClick={auto} className="bg-indigo-700 text-white" data-testid="card-recon-auto">Auto-match</Button></div>
                </div>
                <div className="border border-slate-200 rounded max-h-56 overflow-y-auto text-[11px]">
                  {data.suggested.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 px-2 py-1 border-b border-slate-100 cursor-pointer">
                      <input type="checkbox" checked={!!sel[t.id]} onChange={(e) => setSel((s) => ({ ...s, [t.id]: e.target.checked }))} data-testid={`card-recon-sug-${t.id}`} />
                      <span className="w-16">{t.txn_date}</span><span className="flex-1 truncate">{t.merchant}</span><span>{money(t.amount)}</span>
                    </label>
                  ))}
                  {!data.suggested.length && <div className="px-2 py-4 text-center text-slate-400">No unmatched spends in this period.</div>}
                </div>
              </div>
              <div>
                <div className="text-[12px] font-semibold mb-1">Tied ({data.matched_count})</div>
                <div className="border border-slate-200 rounded max-h-56 overflow-y-auto text-[11px]">
                  {data.tied.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 px-2 py-1 border-b border-slate-100">
                      <span className="w-16">{t.txn_date}</span><span className="flex-1 truncate">{t.merchant}</span><span>{money(t.amount)}</span>
                      <button className="text-rose-500 text-[10px]" onClick={() => untie(t.id)} data-testid={`card-recon-untie-${t.id}`}>untie</button>
                    </div>
                  ))}
                  {!data.tied.length && <div className="px-2 py-4 text-center text-slate-400">Nothing tied yet.</div>}
                </div>
              </div>
            </div>
          </div>
        )}
        {override && (
          <div className="border border-rose-200 bg-rose-50 rounded-lg p-3 space-y-2" data-testid="card-recon-override">
            <div className="text-[12px] text-rose-700">{override.message}</div>
            <div><Label className="text-[12px]">Override reason (required)</Label>
              <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Why confirm despite the variance?" data-testid="card-recon-override-reason" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {override
            ? <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmOverride} data-testid="card-recon-override-confirm">Confirm with override</Button>
            : statement.reconciled
              ? <Button disabled variant="outline" data-testid="card-recon-done">✓ Already reconciled</Button>
              : <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={confirm} data-testid="card-recon-confirm">Confirm reconciliation</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentTab({ rows, onAdd, onChange }) {
  const del = async (id) => {
    const reason = window.prompt("Reason to delete this payment?"); if (!reason) return;
    try { await api.delete(`/card-control/payments/${id}?reason=${encodeURIComponent(reason)}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <TableShell testid="card-payments" addLabel="Record payment" onAdd={onAdd}
      headers={["Voucher", "Date", "Amount", "From bank", "UTR", "Ledger", ""]}>
      {rows.map((p, i) => (
        <tr key={p.id} data-testid={`card-pay-row-${i}`} className="hover:bg-slate-50">
          <td className="px-2 py-1.5 font-mono text-[10px]">{p.number}</td>
          <td className="px-2 py-1.5">{p.payment_date}</td>
          <td className="px-2 py-1.5 text-right font-medium text-rose-700">{money(p.amount)}</td>
          <td className="px-2 py-1.5">{p.from_bank || "—"}</td>
          <td className="px-2 py-1.5 text-[10px]">{p.utr || "—"}</td>
          <td className="px-2 py-1.5 text-[10px]">{p.ledger_txn_id ? <span className="text-emerald-600">Posted</span> : "—"}</td>
          <td className="px-2 py-1.5"><button className="text-rose-500 text-[10px]" onClick={() => del(p.id)} data-testid={`card-pay-del-${i}`}>Delete</button></td>
        </tr>
      ))}
      {!rows.length && <tr><td colSpan={7} className="px-2 py-8 text-center text-slate-400">No payments.</td></tr>}
    </TableShell>
  );
}

function RewardTab({ cardId, onAdd }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get(`/card-control/rewards?card_id=${cardId}`); setData(data); } catch { /* */ } })(); }, [cardId]);
  return (
    <div>
      {data?.balances && Object.keys(data.balances).length > 0 && (
        <div className="flex gap-2 mb-3">
          {Object.entries(data.balances).map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-200 px-3 py-1.5 text-[12px]"><Gift className="h-3.5 w-3.5 inline mr-1 text-indigo-600" />{k}: <b>{v}</b></div>
          ))}
        </div>
      )}
      <TableShell testid="card-rewards" addLabel="Add reward event" onAdd={onAdd}
        headers={["Date", "Type", "Event", "Quantity", "Value", "Expiry"]}>
        {(data?.items || []).map((r, i) => (
          <tr key={r.id} data-testid={`card-reward-row-${i}`} className="hover:bg-slate-50">
            <td className="px-2 py-1.5">{r.date}</td><td className="px-2 py-1.5 text-[10px]">{r.reward_type}</td>
            <td className="px-2 py-1.5 text-[10px]">{r.event}</td><td className="px-2 py-1.5 text-right">{r.quantity}</td>
            <td className="px-2 py-1.5 text-right">{r.value_inr != null ? money(r.value_inr) : "—"}</td>
            <td className="px-2 py-1.5">{r.expiry_date || "—"}</td>
          </tr>
        ))}
        {!(data?.items || []).length && <tr><td colSpan={6} className="px-2 py-8 text-center text-slate-400">No reward events.</td></tr>}
      </TableShell>
    </div>
  );
}

function SubTab({ rows, onAdd }) {
  return (
    <TableShell testid="card-subs" addLabel="Add subscription" onAdd={onAdd}
      headers={["Merchant", "Amount", "Frequency", "Next date", "Use", "Status"]}>
      {rows.map((s, i) => (
        <tr key={s.id} data-testid={`card-sub-row-${i}`} className="hover:bg-slate-50">
          <td className="px-2 py-1.5"><Repeat className="h-3.5 w-3.5 inline mr-1 text-slate-400" />{s.merchant}</td>
          <td className="px-2 py-1.5 text-right">{money(s.amount)}</td>
          <td className="px-2 py-1.5 text-[10px]">{s.frequency}</td>
          <td className="px-2 py-1.5">{s.next_date || "—"}</td>
          <td className="px-2 py-1.5 text-[10px]">{s.use}</td>
          <td className="px-2 py-1.5"><span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100">{s.status}</span></td>
        </tr>
      ))}
      {!rows.length && <tr><td colSpan={6} className="px-2 py-8 text-center text-slate-400">No subscriptions.</td></tr>}
    </TableShell>
  );
}

function EmiTab({ rows, onAdd }) {
  return (
    <TableShell testid="card-emis" addLabel="Add EMI plan" onAdd={onAdd}
      headers={["Merchant", "Original", "Interest %", "Tenure", "Monthly", "Start", "End", "Status"]}>
      {rows.map((e, i) => (
        <tr key={e.id} data-testid={`card-emi-row-${i}`} className="hover:bg-slate-50">
          <td className="px-2 py-1.5">{e.merchant}</td>
          <td className="px-2 py-1.5 text-right">{money(e.original_amount)}</td>
          <td className="px-2 py-1.5 text-right">{e.interest_pct}%</td>
          <td className="px-2 py-1.5 text-right">{e.tenure_months}m</td>
          <td className="px-2 py-1.5 text-right font-medium">{money(e.monthly_amount)}</td>
          <td className="px-2 py-1.5">{e.start_date}</td><td className="px-2 py-1.5">{e.end_date || "—"}</td>
          <td className="px-2 py-1.5"><span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100">{e.status}</span></td>
        </tr>
      ))}
      {!rows.length && <tr><td colSpan={8} className="px-2 py-8 text-center text-slate-400">No EMI plans.</td></tr>}
    </TableShell>
  );
}

function Modal({ title, children, onClose, onSave, busy, testid, saveLabel = "Save" }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={onSave} disabled={busy} data-testid={`${testid}-save`}>{busy ? "Saving…" : saveLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Fld = ({ label, span, children }) => (
  <div className={span ? "col-span-2" : ""}><Label className="text-[12px]">{label}</Label>{children}</div>
);
const Sel = ({ value, onChange, opts, testid }) => (
  <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {opts.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

function AddCard({ canPersonal, editing, onClose, onSaved }) {
  const [f, setF] = useState(editing
    ? { scope: editing.scope || "BUSINESS", issuer_bank: editing.issuer_bank || "", card_name: editing.card_name || "", name_on_card: editing.name_on_card || "", card_type: editing.card_type || "CREDIT", network: editing.network || "VISA", last4: editing.last4 || (editing.masked_number || "").slice(-4), credit_limit: editing.credit_limit ?? 0, cash_limit: editing.cash_limit ?? "", statement_date: editing.statement_date ?? "", due_date_rule: editing.due_date_rule || "", annual_fee: editing.annual_fee ?? "", annual_fee_month: editing.annual_fee_month ?? "", expiry_month: editing.expiry_month ?? "", expiry_year: editing.expiry_year ?? "", status: editing.status || "ACTIVE", autopay: editing.autopay || false, payment_bank_account: editing.payment_bank_account || "" }
    : { scope: "BUSINESS", issuer_bank: "", card_name: "", name_on_card: "", card_type: "CREDIT", network: "VISA", last4: "", credit_limit: 0, cash_limit: "", statement_date: "", due_date_rule: "", annual_fee: "", annual_fee_month: "", expiry_month: "", expiry_year: "", status: "ACTIVE", autopay: false, payment_bank_account: "" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.issuer_bank.trim() || !f.card_name.trim() || !f.last4.trim()) { toast.error("Issuer, name and last 4 digits are required"); return; }
    setBusy(true);
    try {
      const body = { ...f, credit_limit: Number(f.credit_limit) || 0,
        cash_limit: f.cash_limit === "" ? null : Number(f.cash_limit),
        statement_date: f.statement_date === "" ? null : Number(f.statement_date),
        annual_fee: f.annual_fee === "" ? null : Number(f.annual_fee),
        annual_fee_month: f.annual_fee_month === "" ? null : Number(f.annual_fee_month),
        expiry_month: f.expiry_month === "" ? null : Number(f.expiry_month),
        expiry_year: f.expiry_year === "" ? null : Number(f.expiry_year) };
      if (editing) { await api.patch(`/card-control/cards/${editing.id}`, body); toast.success("Card updated"); }
      else { await api.post("/card-control/cards", body); toast.success("Card added"); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title={editing ? "Edit credit card" : "Add credit card"} onClose={onClose} onSave={save} busy={busy} testid="card-add-dialog" saveLabel={editing ? "Save" : "Add card"}>
      <Fld label="Scope"><Sel value={f.scope} onChange={(v) => u("scope", v)} opts={canPersonal ? ["BUSINESS", "CORPORATE", "PERSONAL"] : ["BUSINESS", "CORPORATE"]} testid="card-scope" /></Fld>
      <Fld label="Network"><Sel value={f.network} onChange={(v) => u("network", v)} opts={NETWORKS} testid="card-network" /></Fld>
      <Fld label="Issuer bank"><Input value={f.issuer_bank} onChange={(e) => u("issuer_bank", e.target.value)} data-testid="card-issuer" /></Fld>
      <Fld label="Card name"><Input value={f.card_name} onChange={(e) => u("card_name", e.target.value)} placeholder="Axis Magnus" data-testid="card-name" /></Fld>
      <Fld label="Name on card"><Input value={f.name_on_card} onChange={(e) => u("name_on_card", e.target.value)} placeholder="e.g. PD, CD, MLD" data-testid="card-name-on-card" /></Fld>
      <Fld label="Card type"><Sel value={f.card_type} onChange={(v) => u("card_type", v)} opts={CARD_TYPES} testid="card-type" /></Fld>
      <Fld label="Last 4 digits"><Input value={f.last4} maxLength={4} onChange={(e) => u("last4", e.target.value)} placeholder="4821" data-testid="card-last4" /></Fld>
      <Fld label="Card status"><Sel value={f.status} onChange={(v) => u("status", v)} opts={["ACTIVE", "INACTIVE"]} testid="card-status" /></Fld>
      <Fld label="Expiry month"><Input type="number" value={f.expiry_month} onChange={(e) => u("expiry_month", e.target.value)} data-testid="card-expiry-month" /></Fld>
      <Fld label="Expiry year"><Input type="number" value={f.expiry_year} onChange={(e) => u("expiry_year", e.target.value)} data-testid="card-expiry-year" /></Fld>
    </Modal>
  );
}

function AddTxn({ cardId, categories, onClose, onSaved }) {
  const [f, setF] = useState({ txn_date: todayStr(), merchant: "", amount: "", kind: "PURCHASE", category: "", use: "BUSINESS", expense_purpose: "", reference: "" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.merchant.trim() || !f.amount) { toast.error("Merchant and amount required"); return; }
    setBusy(true);
    try { await api.post("/card-control/txns", { card_id: cardId, ...f, amount: Number(f.amount) }); toast.success("Spend recorded"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add card spend" onClose={onClose} onSave={save} busy={busy} testid="card-txn-dialog">
      <Fld label="Date"><Input type="date" value={f.txn_date} onChange={(e) => u("txn_date", e.target.value)} data-testid="card-txn-date" /></Fld>
      <Fld label="Amount"><Input type="number" value={f.amount} onChange={(e) => u("amount", e.target.value)} data-testid="card-txn-amount" /></Fld>
      <Fld label="Merchant" span><Input value={f.merchant} onChange={(e) => u("merchant", e.target.value)} data-testid="card-txn-merchant" /></Fld>
      <Fld label="Kind"><Sel value={f.kind} onChange={(v) => u("kind", v)} opts={["PURCHASE", "REFUND", "FEE", "INTEREST", "CASHBACK"]} testid="card-txn-kind" /></Fld>
      <Fld label="Category"><Sel value={f.category} onChange={(v) => u("category", v)} opts={["", ...(categories || [])]} /></Fld>
      <Fld label="Use"><Sel value={f.use} onChange={(v) => u("use", v)} opts={["BUSINESS", "PERSONAL"]} /></Fld>
      <Fld label="Reference"><Input value={f.reference} onChange={(e) => u("reference", e.target.value)} /></Fld>
      <Fld label="Expense purpose" span><Input value={f.expense_purpose} onChange={(e) => u("expense_purpose", e.target.value)} /></Fld>
    </Modal>
  );
}

function AddPayment({ cardId, statements, onClose, onSaved }) {
  const [f, setF] = useState({ statement_id: "", payment_date: todayStr(), amount: "", from_bank: "", utr: "" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.amount) { toast.error("Amount required"); return; }
    setBusy(true);
    try { await api.post("/card-control/payments", { card_id: cardId, statement_id: f.statement_id || null, payment_date: f.payment_date, amount: Number(f.amount), from_bank: f.from_bank, utr: f.utr }); toast.success("Payment recorded & posted to ONE Ledger"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Record card payment" onClose={onClose} onSave={save} busy={busy} testid="card-pay-dialog">
      <Fld label="Against statement" span>
        <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.statement_id} onChange={(e) => u("statement_id", e.target.value)} data-testid="card-pay-statement">
          <option value="">— none (ad-hoc) —</option>
          {(statements || []).map((s) => <option key={s.id} value={s.id}>{s.code} · due {s.due_date} · {money(s.outstanding)}</option>)}
        </select>
      </Fld>
      <Fld label="Payment date"><Input type="date" value={f.payment_date} onChange={(e) => u("payment_date", e.target.value)} data-testid="card-pay-date" /></Fld>
      <Fld label="Amount"><Input type="number" value={f.amount} onChange={(e) => u("amount", e.target.value)} data-testid="card-pay-amount" /></Fld>
      <Fld label="From bank"><Input value={f.from_bank} onChange={(e) => u("from_bank", e.target.value)} /></Fld>
      <Fld label="UTR / reference"><Input value={f.utr} onChange={(e) => u("utr", e.target.value)} /></Fld>
    </Modal>
  );
}

function AddReward({ cardId, onClose, onSaved }) {
  const [f, setF] = useState({ date: todayStr(), reward_type: "POINTS", event: "EARNED", quantity: "", value_inr: "", expiry_date: "" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.post("/card-control/rewards", { card_id: cardId, date: f.date, reward_type: f.reward_type, event: f.event, quantity: Number(f.quantity) || 0, value_inr: f.value_inr === "" ? null : Number(f.value_inr), expiry_date: f.expiry_date || null }); toast.success("Reward event added"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add reward event" onClose={onClose} onSave={save} busy={busy} testid="card-reward-dialog">
      <Fld label="Date"><Input type="date" value={f.date} onChange={(e) => u("date", e.target.value)} /></Fld>
      <Fld label="Type"><Sel value={f.reward_type} onChange={(v) => u("reward_type", v)} opts={["POINTS", "CASHBACK", "MILES", "HOTEL"]} /></Fld>
      <Fld label="Event"><Sel value={f.event} onChange={(v) => u("event", v)} opts={["EARNED", "REDEEMED", "EXPIRED", "ADJUSTED"]} /></Fld>
      <Fld label="Quantity"><Input type="number" value={f.quantity} onChange={(e) => u("quantity", e.target.value)} data-testid="card-reward-qty" /></Fld>
      <Fld label="Value (₹)"><Input type="number" value={f.value_inr} onChange={(e) => u("value_inr", e.target.value)} /></Fld>
      <Fld label="Expiry date"><Input type="date" value={f.expiry_date} onChange={(e) => u("expiry_date", e.target.value)} /></Fld>
    </Modal>
  );
}

function AddSubscription({ cardId, onClose, onSaved }) {
  const [f, setF] = useState({ merchant: "", amount: "", frequency: "MONTHLY", next_date: "", category: "", use: "BUSINESS", status: "ACTIVE" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.merchant.trim() || !f.amount) { toast.error("Merchant and amount required"); return; }
    setBusy(true);
    try { await api.post("/card-control/subscriptions", { card_id: cardId, ...f, amount: Number(f.amount), next_date: f.next_date || null }); toast.success("Subscription added"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add subscription" onClose={onClose} onSave={save} busy={busy} testid="card-sub-dialog">
      <Fld label="Merchant" span><Input value={f.merchant} onChange={(e) => u("merchant", e.target.value)} data-testid="card-sub-merchant" /></Fld>
      <Fld label="Amount"><Input type="number" value={f.amount} onChange={(e) => u("amount", e.target.value)} data-testid="card-sub-amount" /></Fld>
      <Fld label="Frequency"><Sel value={f.frequency} onChange={(v) => u("frequency", v)} opts={["MONTHLY", "QUARTERLY", "YEARLY"]} /></Fld>
      <Fld label="Next date"><Input type="date" value={f.next_date} onChange={(e) => u("next_date", e.target.value)} /></Fld>
      <Fld label="Use"><Sel value={f.use} onChange={(v) => u("use", v)} opts={["BUSINESS", "PERSONAL"]} /></Fld>
      <Fld label="Category"><Input value={f.category} onChange={(e) => u("category", e.target.value)} /></Fld>
    </Modal>
  );
}

function AddEmi({ cardId, onClose, onSaved }) {
  const [f, setF] = useState({ merchant: "", original_amount: "", interest_pct: 0, tenure_months: "", monthly_amount: "", start_date: todayStr() });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.merchant.trim() || !f.original_amount || !f.tenure_months) { toast.error("Merchant, amount and tenure required"); return; }
    setBusy(true);
    try { await api.post("/card-control/emis", { card_id: cardId, merchant: f.merchant, original_amount: Number(f.original_amount), interest_pct: Number(f.interest_pct) || 0, tenure_months: Number(f.tenure_months), monthly_amount: Number(f.monthly_amount) || 0, start_date: f.start_date }); toast.success("EMI plan added"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add EMI plan" onClose={onClose} onSave={save} busy={busy} testid="card-emi-dialog">
      <Fld label="Merchant" span><Input value={f.merchant} onChange={(e) => u("merchant", e.target.value)} data-testid="card-emi-merchant" /></Fld>
      <Fld label="Original amount"><Input type="number" value={f.original_amount} onChange={(e) => u("original_amount", e.target.value)} data-testid="card-emi-amount" /></Fld>
      <Fld label="Interest %"><Input type="number" value={f.interest_pct} onChange={(e) => u("interest_pct", e.target.value)} /></Fld>
      <Fld label="Tenure (months)"><Input type="number" value={f.tenure_months} onChange={(e) => u("tenure_months", e.target.value)} data-testid="card-emi-tenure" /></Fld>
      <Fld label="Monthly (auto if blank)"><Input type="number" value={f.monthly_amount} onChange={(e) => u("monthly_amount", e.target.value)} /></Fld>
      <Fld label="Start date"><Input type="date" value={f.start_date} onChange={(e) => u("start_date", e.target.value)} /></Fld>
    </Modal>
  );
}

function ImportTxns({ cardId, onClose, onSaved }) {
  const [batch, setBatch] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const pdf = /\.pdf$/i.test(file.name);
    setBusy(true);
    try {
      const { data } = await api.post(`/card-control/txns/import/upload?card_id=${cardId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data); setPreview(null);
      if (data.suggested_mapping && Object.keys(data.suggested_mapping).length) setMapping(data.suggested_mapping);
      toast.success(pdf ? `Read ${data.row_count} transactions from PDF — review the mapping` : "File read — map columns");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  const doPreview = async () => {
    try { await api.post(`/card-control/txns/import/${batch.id}/mapping`, { mapping }); const { data } = await api.get(`/card-control/txns/import/${batch.id}/preview`); setPreview(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const commit = async () => {
    try { const { data } = await api.post(`/card-control/txns/import/${batch.id}/commit`); toast.success(`Imported ${data.imported} transactions`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="card-import-dialog">
        <DialogHeader><DialogTitle>Import card transactions</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="border border-slate-200 rounded-lg p-3">
            <p className="text-[12px] text-slate-500 mb-2">Upload a CSV/XLSX export or a PDF statement. PDF statements are read automatically (including scanned ones). The original file is kept as evidence; nothing posts without your review. Duplicates are detected by date + amount + merchant.</p>
            <input type="file" accept=".csv,.xlsx,.xls,.pdf" onChange={upload} disabled={busy} data-testid="card-import-file" className="text-[12px]" />
            {busy && <div className="flex items-center gap-2 text-[12px] text-indigo-600 mt-2" data-testid="card-import-reading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading file… PDF statements may take a few seconds.</div>}
          </div>
          {batch && (
            <div className="border border-slate-200 rounded-lg p-3" data-testid="card-import-mapping">
              <div className="font-semibold text-sm mb-2">Map columns — {batch.code}</div>
              <div className="grid grid-cols-3 gap-2">
                {batch.target_fields.map((tf) => (
                  <div key={tf}><Label className="text-[11px] capitalize">{tf.replace("_", " ")}</Label>
                    <select className="w-full h-8 border border-slate-200 rounded-md px-2 text-[11px] bg-white" value={mapping[tf] || ""} onChange={(e) => setMapping((m) => ({ ...m, [tf]: e.target.value }))} data-testid={`card-import-map-${tf}`}>
                      <option value="">—</option>{batch.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select></div>
                ))}
              </div>
              <Button className="mt-3" size="sm" onClick={doPreview} data-testid="card-import-preview-btn">Preview</Button>
            </div>
          )}
          {preview && (
            <div className="border border-slate-200 rounded-lg p-3" data-testid="card-import-preview">
              <div className="flex justify-between mb-2">
                <div className="font-semibold text-[13px]">{preview.row_count} rows · {preview.valid} valid · {preview.duplicates} duplicates</div>
                <Button size="sm" className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={commit} data-testid="card-import-commit-btn">Commit import</Button>
              </div>
              <div className="overflow-x-auto max-h-64">
                <table className="w-full text-[11px]"><thead className="bg-slate-50 text-slate-500 uppercase text-[9px]"><tr>{["Date", "Merchant", "Amount", "Use", "Dup"].map((h) => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.rows.map((r, i) => (
                      <tr key={i} className={r.duplicate ? "bg-amber-50" : ""}>
                        <td className="px-2 py-1">{r.txn_date}</td><td className="px-2 py-1">{r.merchant}</td>
                        <td className="px-2 py-1 text-right">{money(r.amount)}</td><td className="px-2 py-1">{r.use}</td>
                        <td className="px-2 py-1">{r.duplicate ? "DUP" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
